import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDownRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1592317460245-765f641e122b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const line = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.4 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

function MaskLine({ children, i }) {
  return (
    <span className="line-mask">
      <motion.span custom={i} variants={line} initial="hidden" animate="visible" className="block">
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen pt-28 pb-16 md:pt-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-end">
          {/* Text — asymmetric, left aligned */}
          <div className="lg:col-span-7 lg:pr-6">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="mb-8 flex items-center gap-3 text-[0.72rem] uppercase tracking-[0.22em] text-emerald-900/60"
            >
              <span className="inline-block h-px w-10 bg-emerald-900/40" />
              Private family investment · Est. Ireland
            </motion.p>

            <h1 className="font-serif text-emerald-900 leading-[0.98] tracking-tight text-4xl sm:text-5xl lg:text-6xl">
              <MaskLine i={0}>Investing with</MaskLine>
              <MaskLine i={1}><span className="italic text-emerald-900/70">perspective.</span></MaskLine>
              <MaskLine i={2}>Rooted in Ireland,</MaskLine>
              <MaskLine i={3}>reaching across borders.</MaskLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.25, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 max-w-xl text-base md:text-lg leading-relaxed text-base-text/80"
            >
              TILT Ventures is a private family investment firm. We back creative ideas and
              international opportunities that promise sustainable, reasonable returns—while
              always keeping one eye on the impact we make back home.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.45, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10"
            >
              <button
                data-testid="hero-cta-button"
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                className="group inline-flex items-center gap-3 bg-emerald-900 px-8 py-4 text-sm uppercase tracking-[0.18em] text-base-bg"
              >
                Get in Touch
                <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
              </button>
            </motion.div>
          </div>

          {/* Image — clipped rectangular frame, parallax */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 1.06 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden aspect-[4/5] border border-emerald-900/20"
            >
              <motion.img
                src={HERO_IMG}
                alt="Irish rolling hills meeting distant horizon"
                style={{ y: imgY, scale: imgScale }}
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/40 to-transparent" aria-hidden="true" />
              <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between border-t border-base-bg/20 bg-emerald-950/30 px-5 py-3 text-[0.68rem] uppercase tracking-[0.2em] text-base-bg/90 backdrop-blur-sm">
                <span>53.3°N — Ireland</span>
                <span>Global reach</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
